import { DateOnlyInput } from "../components/DateOnlyInput";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "../api";
import { alphaSave } from "../ui/alpha-save";
import { ErrorState } from "../ui/State";
export function SupplierPerformanceAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building") ?? "",
    scope = "?building=" + building,
    [model, setModel] = useState<any>(),
    [report, setReport] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const pending = useRef<object | null>(null);
  const load = () =>
    api<any>("/api/alpha/supplier-performance" + scope).then(setModel);
  useEffect(() => {
    setReport(undefined);
    setModel(undefined);
    if (building) void load().catch(setError);
  }, [building]);
  async function compare(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      setReport(
        await api(
          "/api/alpha/supplier-performance/" +
            f.get("supplier") +
            scope +
            "&from=" +
            f.get("from") +
            "&to=" +
            f.get("to"),
        ),
      );
      setError(undefined);
    } catch (e) {
      setError(e);
    }
  }
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    pending.current ??= {
      from: report.snapshot.from,
      to: report.snapshot.to,
      sourceHash: report.sourceHash,
      rating: f.get("rating"),
      assessment: f.get("assessment"),
      improvementPlan: f.get("plan"),
      dueOn: f.get("due"),
      idempotencyKey: crypto.randomUUID(),
    };
    setBusy(true);
    try {
      await alphaSave(
        pending,
        "/api/alpha/supplier-performance/" +
          report.snapshot.supplier.id +
          "/review" +
          scope,
        { method: "POST", body: JSON.stringify(pending.current) },
      );
      pending.current = null;
      setReport(undefined);
      await load();
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  const s = report?.snapshot;
  return (
    <>
      <h1>Supplier performance review</h1>
      {!building ? (
        <p>
          Choose a building in <Link to="/alpha">Feature previews</Link>.
        </p>
      ) : (
        <>
          {Boolean(error) && <ErrorState error={error} />}
          <form onSubmit={compare}>
            <fieldset disabled={busy || !!pending.current}>
              <label>
                Supplier
                <select name="supplier" required>
                  <option value="">Choose supplier</option>
                  {model?.suppliers.map((v: any) => (
                    <option key={v.id} value={v.id}>
                      {v.legal_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Jobs created from
                <DateOnlyInput name="from" required />
              </label>
              <label>
                Jobs created through
                <DateOnlyInput name="to" required />
              </label>
              <button>Review evidence</button>
            </fieldset>
          </form>
          {s && (
            <section className="panel">
              <h2>{s.supplier.legal_name}</h2>
              <p>{s.coverage}</p>
              <dl>
                {Object.entries(s.metrics)
                  .filter(([k]) => k !== "firstAttendanceDays")
                  .map(([k, v]) => (
                    <div key={k}>
                      <dt>
                        {
                          (
                            {
                              jobs: "Jobs",
                              completed: "Completed",
                              reopened: "Currently reopened",
                              visits: "Recorded visits",
                              attendanceRecorded: "Attendance recorded",
                              attendanceMissing: "Attendance unknown",
                              defectInspections: "Defect inspections",
                            } as Record<string, string>
                          )[k]
                        }
                      </dt>
                      <dd>{String(v)}</dd>
                    </div>
                  ))}
              </dl>
              {s.work.map((w: any) => (
                <section key={w.id}>
                  <h3>
                    <Link to={"/work/" + w.id}>
                      {w.work_number}: {w.title}
                    </Link>
                  </h3>
                  <p>
                    {w.lifecycle_status} · Authorised cost:{" "}
                    {w.authorised_cost ?? "Unknown"}
                  </p>
                  <p>
                    Days to first recorded attendance:{" "}
                    {s.metrics.firstAttendanceDays.find(
                      (t: any) => t.workId === w.id,
                    )?.days ?? "Unknown"}
                  </p>
                  {w.visits.map((v: any) => (
                    <p key={v.id}>
                      Visit scheduled {v.scheduled ?? "Unknown"} · Attended{" "}
                      {v.occurred ?? "Not recorded"} ·{" "}
                      {v.outcome ?? "No outcome"}
                    </p>
                  ))}
                  {w.inspections.map((v: any) => (
                    <p key={v.id}>
                      Inspection: {v.outcome} · {v.date}
                    </p>
                  ))}
                  {w.invoices.map((n: any) => (
                    <p key={n.id}>
                      Invoice {n.reference ?? n.id} · Net {n.net ?? "Unknown"}{" "}
                      {n.currency} · {n.status}
                    </p>
                  ))}
                </section>
              ))}
              <form onSubmit={save}>
                <fieldset disabled={busy || !!pending.current}>
                  <label>
                    Reviewed assessment
                    <select name="rating">
                      <option>Neutral</option>
                      <option>Positive</option>
                      <option>Concern</option>
                    </select>
                  </label>
                  <label>
                    Assessment and evidence gaps
                    <textarea
                      name="assessment"
                      required
                      minLength={10}
                      maxLength={4000}
                    />
                  </label>
                  <label>
                    Improvement or monitoring plan
                    <textarea
                      name="plan"
                      required
                      minLength={10}
                      maxLength={4000}
                    />
                  </label>
                  <label>
                    Follow-up due
                    <DateOnlyInput name="due" required />
                  </label>
                </fieldset>
                <button disabled={busy}>
                  {pending.current
                    ? "Retry same review"
                    : "Save reviewed scorecard and Action"}
                </button>
              </form>
            </section>
          )}
          <h2>Retained reviews</h2>
          {model&&model.reviews.length===0&&<p>No performance reviews have been recorded for this building. Select a supplier and period to review its evidence.</p>}
          {model?.reviews.map((r: any) => (
            <section className="panel" key={r.id}>
              <h3>
                {r.review_snapshot.supplier.legal_name} · {r.rating}
              </h3>
              <p>
                {r.review_snapshot.from} to {r.review_snapshot.to} ·{" "}
                {r.observation}
              </p>
              <p>{r.review_snapshot.improvementPlan}</p>
              <details>
                <summary>Retained evidence</summary>
                <p>{r.review_snapshot.coverage}</p>
                {r.review_snapshot.work.map((w: any) => (
                  <p key={w.id}>
                    <Link to={"/work/" + w.id}>
                      {w.work_number}: {w.title}
                    </Link>{" "}
                    · {w.lifecycle_status}, {w.visits.length} visits,{" "}
                    {w.inspections.length} inspections, {w.invoices.length}{" "}
                    invoices
                  </p>
                ))}
              </details>
              <Link to={"/work/actions/" + r.review_action_id}>
                Open follow-up Action
              </Link>
            </section>
          ))}
        </>
      )}
    </>
  );
}
